/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AuthTokenResponse } from '../models/AuthTokenResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AuthService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Issue a client API access token.
     * This endpoint generates a client-token for API authentication. The endpoint can only be accessed with an ApplicationKey.
     *
     * A client-token authenticates a client with the device API endpoints that requires authentication. Use it by including the token as a Bearer token in the Authorization header.
     *
     * It can be used in client applications, including web browsers and mobile apps. It's an alternativ to the ApplicationID that should be harder to abuse outside your application's browser environment.
     *
     * A token is valid for a limited time, after which it expires. The default lifetime for a token is 1 hour. You can adjust this lifespan up to a maximum of 24 hours using the `ttl` parameter.
     *
     * A token can be configured with the following optional restrictions:
     * - Device connection key
     * - Client IP
     * - Client Origin
     * - Client Referer
     * - Client User-Agent
     * - Client Accept-Language
     * - Client Accept-Encoding
     * - Client Cache-Control
     *
     * Specifying a device connection key ensures that the token can only be used with a specific device.
     *
     * The client values forms a lightweight client fingerprint that makes it harder to abuse the token outside the client environment.
     *
     * **NOTE**: Issuing a client-token for a specific device does not eliminate the need to include the connection key when sending device commands.
     *
     * The `X-Connection-Key` header is still required for device-specific operations, even if the client-token was issued for that device.
     *
     * ### Token Renewal
     *
     * Alongside the token, you receive a **renew** URL. Use this URL to extend your token's validity, ensuring uninterrupted service for long-running applications.
     *
     * The renew URL remains valid as long as the token is valid. Remember to renew before the token expires; post-expiration, both the token and renew URL become invalid, necessitating a new token issue.
     *
     * The renew operation only extends the token's lifespan. It does not alter initial restrictions (device connection key or other client values) set during token issuance.
     *
     * @param ttl The time-to-live (ttl) of the token in seconds. This defines how long the token remains valid. If not specified, a default value is used. Default is 1 hour (3600 seconds). Minimum is 60 seconds, maximum is 24 hours (86400 seconds).
     * @param to The device connection key for which the token is to be issued. Specifying this parameter restricts the use of the token to the specific device.
     * @param ip The client IP address for which the token is to be issued. Specifying this parameter restricts the use of the token to requests originating from the specific IP address.
     * @param origin The client Origin header value prefix for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific Origin header value prefix.
     * @param r The client Referer header value prefix for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific Referer header value prefix.
     * @param ua The client User-Agent header value prefix for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific User-Agent header value prefix.
     * @param al The client Accept-Language header value for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific Accept-Language header value.
     * @param ae The client Accept-Encoding header value for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific Accept-Encoding header value.
     * @param cc The client Cache-Control header value for which the token is to be issued. Specifying this parameter restricts the use of the token to requests with the specific Cache-Control header value.
     * @returns AuthTokenResponse The issued token.
     * @throws ApiError
     */
    public issueToken(
        ttl: number = 3600,
        to?: string,
        ip?: string,
        origin?: string,
        r?: string,
        ua?: string,
        al?: string,
        ae?: string,
        cc?: string,
    ): CancelablePromise<AuthTokenResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/token/issue',
            query: {
                'ttl': ttl,
                'to': to,
                'ip': ip,
                'origin': origin,
                'r': r,
                'ua': ua,
                'al': al,
                'ae': ae,
                'cc': cc,
            },
            errors: {
                400: `Bad request.`,
                401: `Unauthorized`,
                403: `Forbidden`,
                408: `Request timeout.`,
                500: `Internal server error.`,
            },
        });
    }

}
